import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { apiService, MyVoiceStatus } from '../../../services/api.service';
import VoiceRecord from '../../speakers/components/voice-record.component';
import { GreenTick, TrashIcon, UploadIcon } from '../../../assets';

const cn = createCn('profile', classes);

function formatDate(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Блок «Мой голос» личного кабинета (задача SP1): запись/перезапись/удаление голосового
 *  профиля, привязанного к текущему аккаунту (backend/app/api/speakers.py: /my-voice).
 *  Переиспользует VoiceRecord (frontend/src/pages/speakers/components/voice-record.component.tsx)
 *  для записи с микрофона — тот же компонент, что и в конструкторе спикера. */
const MyVoiceSection = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<MyVoiceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [rerecording, setRerecording] = useState(false);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getMyVoice();
      setStatus(data);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('profile.voice-load-error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVoiceFile(e.target.files?.[0] ?? null);
  };

  const resetForm = () => {
    setVoiceFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!voiceFile || saving) return;
    setSaving(true);
    try {
      const data = await apiService.uploadMyVoice(voiceFile);
      setStatus(data);
      setRerecording(false);
      resetForm();
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <GreenTick />
          {t('profile.voice-saved')}
        </div>,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('profile.voice-save-error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('profile.voice-delete-confirm'))) return;
    try {
      await apiService.deleteMyVoice();
      setStatus({ has_voice: false });
      toast.success(t('profile.voice-deleted'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('profile.voice-delete-error'));
    }
  };

  const handleCancelRerecord = () => {
    setRerecording(false);
    resetForm();
  };

  if (loading) {
    return <div className={cn('voice-status')}>{t('profile.voice-loading')}</div>;
  }

  const showForm = !status?.has_voice || rerecording;

  return (
    <div className={cn('voice-section')}>
      {showForm ? (
        <>
          <p className={cn('voice-hint')}>{t('profile.voice-hint')}</p>

          <VoiceRecord value={voiceFile} onChange={(file) => setVoiceFile(file)} />

          <div className={cn('voice-upload-alt')}>
            <span className={cn('voice-upload-alt-label')}>{t('speakers.or')}</span>
            <label className={cn('voice-upload-box')}>
              <UploadIcon />
              <span>{voiceFile ? voiceFile.name : t('speakers.upload-file')}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.ogg,.m4a,.mp4,.webm,.m4v"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </label>
          </div>

          <div className={cn('voice-actions')}>
            <button
              type="button"
              className={cn('voice-save-btn')}
              onClick={handleSave}
              disabled={!voiceFile || saving}
            >
              {saving ? t('speakers.saving') : t('action.save')}
            </button>
            {status?.has_voice && (
              <button type="button" className={cn('voice-cancel-btn')} onClick={handleCancelRerecord}>
                {t('action.cancel')}
              </button>
            )}
          </div>
        </>
      ) : (
        <div className={cn('voice-status-card')}>
          <div className={cn('voice-status-info')}>
            <GreenTick />
            <div>
              <div className={cn('voice-status-title')}>{t('profile.voice-recorded')}</div>
              {status?.created_at && (
                <div className={cn('voice-status-date')}>
                  {t('profile.voice-recorded-on', { date: formatDate(status.created_at) })}
                </div>
              )}
            </div>
          </div>
          <div className={cn('voice-status-actions')}>
            <button type="button" className={cn('voice-rerecord-btn')} onClick={() => setRerecording(true)}>
              {t('profile.voice-rerecord')}
            </button>
            <button type="button" className={cn('voice-delete-btn')} onClick={handleDelete}>
              <TrashIcon />
              {t('action.delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyVoiceSection;
